import type { components } from '@joyful/openapi/types.gen'
import {
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  Ellipsis,
} from '../../../../core'
import { getNavbarAppHref } from '../../../components/navbar/Navbar'
import { HelpActionMenuItems } from '../../feedback/components/HelpActionMenuItems'
import { useHasUserCapability } from '../../../hooks/useHasUserCapability'
import { getAnnouncementLaunchLabel } from '../getAnnouncementLaunchLabel'
import { useAnnouncementsPath } from '../hooks/useAnnouncementsPath'
import { useAnnouncementsQuery } from '../hooks/useAnnouncementsQuery'
import { useUnseenAnnouncements } from '../hooks/useUnseenAnnouncements'

type Announcement = components['schemas']['AnnouncementRead']

const EMPTY_ANNOUNCEMENTS: Array<Announcement> = []
const ANNOUNCEMENT_DROPDOWN_LIMIT = 3

export interface HelpAnnouncementsDropdownMenuItemProps {
  onSelectAnnouncement: (announcement: Announcement) => void
}

export function HelpAnnouncementsDropdownMenuItem({
  onSelectAnnouncement,
}: HelpAnnouncementsDropdownMenuItemProps) {
  const hasCapability = useHasUserCapability()
  const canViewAllAnnouncements = hasCapability('announcement.view.all')
  const canAccessManageAnnouncements =
    hasCapability('announcement.manage') && canViewAllAnnouncements
  const announcementsPath = useAnnouncementsPath()
  const { data } = useAnnouncementsQuery()
  const { markSeen } = useUnseenAnnouncements()
  const announcements =
    data?.announcements.slice(0, ANNOUNCEMENT_DROPDOWN_LIMIT) ??
    EMPTY_ANNOUNCEMENTS

  return (
    <DropdownMenuSub
      onOpenChange={(open) => {
        if (open && announcements.length > 0) {
          markSeen()
        }
      }}
    >
      <DropdownMenuSubTrigger>{`Help`}</DropdownMenuSubTrigger>
      <DropdownMenuPortal>
        <DropdownMenuSubContent className="w-72">
          <HelpActionMenuItems />
          {announcements.length > 0 ||
          canAccessManageAnnouncements ||
          (announcementsPath !== null && canViewAllAnnouncements) ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                {announcements.length > 0 ? (
                  <DropdownMenuLabel className="py-1 pl-2">{`What's new`}</DropdownMenuLabel>
                ) : null}
                {announcements.map((announcement, index) => {
                  return (
                    <DropdownMenuItem
                      key={announcement.id}
                      className="group/item relative"
                      icon={
                        <span className="relative flex items-center justify-center self-stretch">
                          {index === 0 ? null : (
                            <span
                              aria-hidden
                              className="bg-tremor-content-subtle absolute top-0 bottom-1/2 left-1/2 w-px -translate-x-1/2"
                            />
                          )}
                          {index === announcements.length - 1 ? null : (
                            <span
                              aria-hidden
                              className="bg-tremor-content-subtle absolute top-1/2 bottom-0 left-1/2 w-px -translate-x-1/2"
                            />
                          )}
                          <span className="bg-tremor-content-subtle relative z-10 size-1.5 rounded-full" />
                        </span>
                      }
                      endAdornment={
                        <span className="text-tremor-content text-xs">
                          {getAnnouncementLaunchLabel({
                            formatDateOptions: {
                              shouldSkipCurrentYear: true,
                            },
                            launchDate: announcement.launch_date,
                            rolloutStrategy: announcement.schedule.strategy,
                          })}
                        </span>
                      }
                      shouldCloseOnSelect
                      onSelect={() => {
                        onSelectAnnouncement(announcement)
                      }}
                    >
                      <Ellipsis>{announcement.title}</Ellipsis>
                    </DropdownMenuItem>
                  )
                })}

                {announcements.length > 0 ? <DropdownMenuSeparator /> : null}
                {announcementsPath !== null &&
                (canViewAllAnnouncements || announcements.length > 0) ? (
                  <DropdownMenuItem shouldCloseOnSelect asChild>
                    <a href={announcementsPath}>{`View all announcements`}</a>
                  </DropdownMenuItem>
                ) : null}
                {canAccessManageAnnouncements ? (
                  <DropdownMenuItem shouldCloseOnSelect asChild>
                    <a
                      href={getNavbarAppHref({
                        app: 'internal',
                        path: '/internal/announcements',
                      })}
                    >
                      {`Manage announcements`}
                    </a>
                  </DropdownMenuItem>
                ) : null}
              </DropdownMenuGroup>
            </>
          ) : null}
        </DropdownMenuSubContent>
      </DropdownMenuPortal>
    </DropdownMenuSub>
  )
}
